import React from "react";
import { Link } from "react-router-dom";
import { useEnquiry } from "@/context/EnquiryContext";
import { ArrowUpRight, ArrowRight } from "lucide-react";

export function OrgCard({ org, detailed = false }) {
  const { openEnquiry } = useEnquiry();
  const slug = org.code.toLowerCase();

  const VisitButton = () =>
    org.external ? (
      <a href={org.url} target="_blank" rel="noopener noreferrer" data-testid={`org-visit-${slug}`}
        className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-orange-600 hover:-translate-y-0.5">
        {org.visitLabel} <ArrowUpRight className="h-4 w-4" />
      </a>
    ) : (
      <Link to={org.url} data-testid={`org-visit-${slug}`}
        className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-orange-600 hover:-translate-y-0.5">
        {org.visitLabel} <ArrowRight className="h-4 w-4" />
      </Link>
    );

  return (
    <div data-testid={`org-card-${slug}`}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-xl md:p-8">
      <div className="absolute right-6 top-6 font-mono text-4xl font-bold text-slate-100 transition-colors group-hover:text-orange-100">{org.number}</div>

      <div className={`mb-5 inline-flex h-16 w-fit items-center justify-center overflow-hidden rounded-xl px-4 ring-1 ${org.darkLogo ? "bg-black ring-black/20" : "bg-white ring-slate-200"} ${org.code === "FOUNDATION" ? "!px-2" : ""}`}>
        <img
          src={org.logo}
          alt={`${org.name} logo`}
          className={`w-auto object-contain ${org.code === "FOUNDATION" ? "h-12 rounded-full" : "h-9"}`}
        />
      </div>

      <span className="inline-flex w-fit items-center rounded-full border border-orange-500/20 bg-orange-500/10 px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-wide text-orange-600">
        {org.category}
      </span>

      <h3 className="mt-4 font-display text-xl font-bold text-slate-900">{org.name}</h3>
      <p className="text-sm font-medium text-slate-500">{org.fullName}</p>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{org.description}</p>

      {detailed && org.highlights?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {org.highlights.map((h) => (
            <span key={h} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">{h}</span>
          ))}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <VisitButton />
        <button onClick={() => openEnquiry({ interest: org.interest })} data-testid={`org-enquire-${slug}`}
          className="inline-flex items-center justify-center rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition-all hover:border-orange-400 hover:text-orange-600">
          Request Information
        </button>
      </div>
    </div>
  );
}
